import React from 'react'
import axios from 'axios'
import {
  APIProvider,
  AdvancedMarker,
  InfoWindow,
  Map as GoogleMap,
  Pin,
  useMap,
} from '@vis.gl/react-google-maps'
import {
  MdCalendarToday,
  MdCheckCircle,
  MdChevronLeft,
  MdChevronRight,
  MdContentCopy,
  MdEmail,
  MdHome,
  MdLocationOn,
  MdOpenInNew,
  MdPhone,
} from 'react-icons/md'
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isValid,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns'
import { getRequestHeaders } from '../api'
import { baseUrl } from '../assets/Animations'
import { createAndOrganizeTeachersCalendarEventsForDisplayedPeriodOfTime } from '../Calendar/calendarFunctions'
import { getCalendarEventDisplayName } from '../Calendar/calendarDisplayUtils'
import { normalizeOptimizerAvailability } from '../Scheduling2025/scheduleOptimizer'
import {
  formatMapAddress,
  getLocationFromRecord,
  isValidMapLocation,
} from '../StudentOnboardingCardModalFall2023/MagicScheduler/magicSchedulerUtils'
import { getTeacherImageUrl } from './teacherProfileUtils'
import './teacher-profile.css'

const GOOGLE_MAPS_API_KEY = 'AIzaSyBLfMu_7gTWuHw6C3kOqLB8uBUjP9eNLF8'
const GOOGLE_MAP_ID = '3324feda48bc0f19'
const DEFAULT_MAP_CENTER = { lat: 39.5, lng: -98.35 }
const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const AVAILABILITY_DISPLAY_DAYS = ['Monday', 'Friday', 'Tuesday', 'Saturday', 'Wednesday', 'Sunday', 'Thursday']
const SCHOOL_NAMES = {
  mma: 'Mountainside Music Academy',
  lsma: 'Lakeside Music Academy',
  tma: 'Treeside Music Academy',
  tsma: 'Treeside Music Academy',
  mor: 'Morningside Music Academy',
  psma: 'Parkside Music Academy',
  tem: 'Tempe Music Academy',
  den: 'Denton Music Academy',
  tul: 'Tulsa Music Academy',
}
const locationRequestCache = new Map()

const text = (value) => String(value || '').trim()
const teacherIdFrom = (teacher) => text(
  teacher?.TeacherID || teacher?.teacherId || teacher?.teacherID || teacher?.id
)

const teacherInstrumentsFrom = (teacher) => {
  const instruments = teacher?.InstrumentList || teacher?.instrumentList || teacher?.instruments || []
  return [...new Set((Array.isArray(instruments) ? instruments : [])
    .map((instrument) => text(
      typeof instrument === 'string'
        ? instrument
        : instrument?.name || instrument?.instrument || instrument?.parentInstrument
    ))
    .filter(Boolean))]
}

const schoolNameFrom = (teacher) => {
  const school = text(teacher?.School || teacher?.school)
  return SCHOOL_NAMES[school.toLowerCase()] || school || 'Music Academy'
}

const teacherAddressFrom = (teacher) => teacher?.Address || teacher?.address || {}

const teacherAddressLabelFrom = (teacher) => {
  const address = teacherAddressFrom(teacher)
  return [
    address?.StreetAddress || address?.streetAddress || address?.addressLine1,
    address?.addressLine2,
    [address?.City || address?.city, address?.State || address?.state].filter(Boolean).join(', '),
    address?.Zip || address?.zip || address?.zipcode,
  ].filter(Boolean).join(' ')
}

const minutesToTimeLabel = (minutes) => {
  if (!Number.isFinite(minutes)) return ''
  const normalizedMinutes = Math.max(0, Math.min(24 * 60, minutes))
  const hour24 = Math.floor(normalizedMinutes / 60) % 24
  const minute = normalizedMinutes % 60
  const hour12 = hour24 % 12 || 12
  const suffix = hour24 >= 12 ? 'PM' : 'AM'
  return `${hour12}:${String(minute).padStart(2, '0')}${suffix}`
}

const availabilityLabel = (normalizedAvailability, day) => {
  const dayIndex = DAY_NAMES.indexOf(day)
  const intervals = normalizedAvailability?.byDay?.[dayIndex] || []
  if (intervals.length === 0) return 'Unavailable'
  return intervals.map((interval) => {
    const start = minutesToTimeLabel(interval?.start)
    const end = minutesToTimeLabel(interval?.end)
    return [start, end].filter(Boolean).join(' - ')
  }).filter(Boolean).join(', ') || 'Unavailable'
}

const backgroundCheckLabel = (value) => {
  switch (text(value).toLowerCase()) {
    case 'verified': return 'Passed'
    case 'passed': return 'Passed'
    case 'complete': return 'Passed'
    case 'completed': return 'Passed'
    case 'ordered': return 'Ordered'
    case 'pending': return 'Pending'
    default: return text(value) || 'Not recorded'
  }
}

const teacherStatusFrom = (teacher) => text(
  teacher?.Status || teacher?.status || teacher?.teacherStatus
)

const teacherStatusLabel = (value) => {
  switch (text(value).toLowerCase()) {
    case 'active': return 'Active'
    case 'goon': return 'Probation'
    case 'summer-break': return 'Summer Break'
    case 'leaving': return 'Leaving'
    case 'left school': return 'Left School'
    default: return text(value) || 'Not recorded'
  }
}

const teacherStatusTone = (value) => {
  switch (text(value).toLowerCase()) {
    case 'active': return 'success'
    case 'summer-break': return 'warning'
    case 'goon':
    case 'leaving':
    case 'left school': return 'danger'
    default: return 'neutral'
  }
}

const backgroundCheckFrom = (teacher) => (
  teacher?.BackgroundCheck
  || teacher?.backgroundCheck
  || teacher?.backgroundCheckStatus
  || teacher?.background_check
)

const backgroundCheckTone = (value) => {
  switch (text(value).toLowerCase()) {
    case 'verified':
    case 'passed':
    case 'complete':
    case 'completed': return 'success'
    case 'ordered':
    case 'pending': return 'warning'
    default: return 'neutral'
  }
}

const CopyRow = ({ icon: Icon, label, value }) => {
  const [copied, setCopied] = React.useState(false)
  if (!value) return null

  const copyValue = async () => {
    try {
      await navigator.clipboard?.writeText(value)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1200)
    } catch (error) {
      console.error('Unable to copy teacher information:', error)
    }
  }

  return (
    <div className='atharo-teacher__contact-row'>
      <Icon aria-hidden='true' />
      <span>{value}</span>
      <button type='button' onClick={copyValue} aria-label={`Copy ${label}`} title={copied ? 'Copied' : `Copy ${label}`}>
        {copied ? <MdCheckCircle /> : <MdContentCopy />}
      </button>
    </div>
  )
}

const TeacherContact = ({ teacher }) => {
  const normalizedAvailability = React.useMemo(
    () => normalizeOptimizerAvailability(
      teacher?.AvailabilityDetails
      || teacher?.availabilityDetails
      || teacher?.Availability
      || teacher?.availability
      || teacher?.availabilityByDay
      || {}
    ),
    [teacher]
  )
  const address = teacherAddressLabelFrom(teacher)

  return (
    <div className='atharo-teacher__contact-view'>
      <section className='atharo-teacher__section'>
        <h3>Contact</h3>
        <CopyRow icon={MdEmail} label='email address' value={text(teacher?.Email || teacher?.email)} />
        <CopyRow icon={MdPhone} label='phone number' value={text(teacher?.PhoneNumber || teacher?.phone)} />
        <CopyRow icon={MdHome} label='home address' value={address} />
      </section>

      <section className='atharo-teacher__section'>
        <h3>Availability</h3>
        <div className='atharo-teacher__availability-grid'>
          {AVAILABILITY_DISPLAY_DAYS.map((day) => {
            const label = availabilityLabel(normalizedAvailability, day)
            return (
              <div className={`atharo-teacher__availability ${label === 'Unavailable' ? 'is-unavailable' : ''}`} key={day}>
                <span>{day.slice(0, 3).toUpperCase()}</span>
                <strong>{label}</strong>
              </div>
            )
          })}
        </div>
      </section>

    </div>
  )
}

const normalizeCalendarEvents = (events) => (
  (Array.isArray(events) ? events : [])
    .filter((event) => event && !event.isPendingOffer && !event.isAvailabilityPlaceholder)
    .map((event) => ({
      ...event,
      recurring: event.recurring && typeof event.recurring === 'object'
        ? event.recurring
        : { isOn: false, frequency: 'none', interval: 0 },
      recurringEndDate: event.recurringEndDate || '',
    }))
)

const eventInstrument = (event) => {
  const direct = text(
    event?.instrumentName || event?.instrument || event?.Instrument || event?.lessonInstrument
  )
  if (direct) return direct
  const instruments = event?.instrumentList || event?.InstrumentList || []
  const first = Array.isArray(instruments) ? instruments[0] : null
  return text(typeof first === 'string' ? first : first?.name || first?.parentInstrument)
}

const eventName = (event) => getCalendarEventDisplayName(
  event?.name || event?.studentName || event?.familyName || 'Lesson'
)

const TeacherLessonRow = ({ event }) => {
  const start = new Date(event?.dateTime)
  const detail = [
    eventInstrument(event),
    Number(event?.lessonLength) > 0 ? `${event.lessonLength} min` : '',
  ].filter(Boolean).join(' · ')

  return (
    <div className='atharo-teacher__lesson-row'>
      <time>{isValid(start) ? format(start, 'h:mm a') : text(event?.time)}</time>
      <span>
        <strong>{eventName(event)}</strong>
        {detail && <small>{detail}</small>}
      </span>
    </div>
  )
}

const buildCalendar = (days, events) => {
  let calendar = {}
  try {
    createAndOrganizeTeachersCalendarEventsForDisplayedPeriodOfTime(
      days,
      normalizeCalendarEvents(events),
      (result) => { calendar = result || {} }
    )
  } catch (error) {
    console.error('Unable to organize teacher calendar events:', error)
  }
  return calendar
}

const TeacherSchedule = ({ events, loading, error }) => {
  const today = React.useMemo(() => startOfDay(new Date()), [])
  const [mode, setMode] = React.useState('schedule')
  const [selectedDate, setSelectedDate] = React.useState(today)
  const [visibleMonth, setVisibleMonth] = React.useState(startOfMonth(today))

  const monthDays = React.useMemo(() => eachDayOfInterval({
    start: startOfWeek(startOfMonth(visibleMonth)),
    end: endOfWeek(endOfMonth(visibleMonth)),
  }), [visibleMonth])
  const scheduleDays = React.useMemo(
    () => eachDayOfInterval({ start: today, end: addDays(today, 60) }),
    [today]
  )
  const displayedDays = React.useMemo(() => {
    const byDate = new Map()
    ;[...scheduleDays, ...monthDays].forEach((day) => byDate.set(format(day, 'yyyy-MM-dd'), day))
    return Array.from(byDate.values()).sort((left, right) => left - right)
  }, [monthDays, scheduleDays])
  const calendar = React.useMemo(
    () => buildCalendar(displayedDays, events),
    [displayedDays, events]
  )
  const daysWithLessons = scheduleDays.filter(
    (day) => (calendar[format(day, 'yyyy-MM-dd')]?.events || []).length > 0
  )
  const selectedLessons = calendar[format(selectedDate, 'yyyy-MM-dd')]?.events || []

  const goToToday = () => {
    setSelectedDate(today)
    setVisibleMonth(startOfMonth(today))
  }

  return (
    <div className='atharo-teacher__schedule-view'>
      <div className='atharo-teacher__schedule-toolbar'>
        <div className='atharo-teacher__schedule-modes' aria-label='Schedule display'>
          <button type='button' className={mode === 'schedule' ? 'is-active' : ''} onClick={() => setMode('schedule')}>Schedule</button>
          <button type='button' className={mode === 'month' ? 'is-active' : ''} onClick={() => setMode('month')}>Month</button>
        </div>
        <button type='button' className='atharo-teacher__today-button' onClick={goToToday}>
          <MdCalendarToday aria-hidden='true' /> Today
        </button>
      </div>

      {loading && <div className='atharo-teacher__message'>Loading schedule…</div>}
      {!loading && error && <div className='atharo-teacher__message is-error'>{error}</div>}

      {!loading && !error && mode === 'schedule' && (
        <div className='atharo-teacher__lesson-groups'>
          {daysWithLessons.slice(0, 21).map((day) => (
            <section key={format(day, 'yyyy-MM-dd')}>
              <h3>{format(day, 'EEEE, MMM d')}</h3>
              {(calendar[format(day, 'yyyy-MM-dd')]?.events || [])
                .sort((left, right) => new Date(left.dateTime) - new Date(right.dateTime))
                .map((event, index) => (
                  <TeacherLessonRow event={event} key={event.id || event._id || `${event.dateTime}-${index}`} />
                ))}
            </section>
          ))}
          {daysWithLessons.length === 0 && (
            <div className='atharo-teacher__message'>No lessons are scheduled in the next 60 days.</div>
          )}
        </div>
      )}

      {!loading && !error && mode === 'month' && (
        <div className='atharo-teacher__month-view'>
          <div className='atharo-teacher__month-heading'>
            <button type='button' aria-label='Previous month' onClick={() => setVisibleMonth((month) => subMonths(month, 1))}>
              <MdChevronLeft />
            </button>
            <strong>{format(visibleMonth, 'MMMM yyyy')}</strong>
            <button type='button' aria-label='Next month' onClick={() => setVisibleMonth((month) => addMonths(month, 1))}>
              <MdChevronRight />
            </button>
          </div>
          <div className='atharo-teacher__month-grid'>
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => <span className='is-weekday' key={day}>{day}</span>)}
            {monthDays.map((day) => {
              const lessons = calendar[format(day, 'yyyy-MM-dd')]?.events || []
              return (
                <button
                  type='button'
                  key={format(day, 'yyyy-MM-dd')}
                  className={`${isSameMonth(day, visibleMonth) ? '' : 'is-outside'} ${isSameDay(day, selectedDate) ? 'is-selected' : ''}`}
                  onClick={() => setSelectedDate(day)}
                  aria-label={`${format(day, 'MMMM d')}, ${lessons.length} lesson${lessons.length === 1 ? '' : 's'}`}
                >
                  <span>{format(day, 'd')}</span>
                  {lessons.length > 0 && <i aria-hidden='true' />}
                </button>
              )
            })}
          </div>
          <section className='atharo-teacher__selected-day'>
            <h3>Schedule for {format(selectedDate, 'MMM do')}</h3>
            {selectedLessons
              .sort((left, right) => new Date(left.dateTime) - new Date(right.dateTime))
              .map((event, index) => (
                <TeacherLessonRow event={event} key={event.id || event._id || `${event.dateTime}-${index}`} />
              ))}
            {selectedLessons.length === 0 && <div className='atharo-teacher__message'>No lessons scheduled.</div>}
          </section>
        </div>
      )}
    </div>
  )
}

const resolveLocation = async (address) => {
  const normalizedAddress = text(address)
  if (!normalizedAddress) return null
  const cacheKey = normalizedAddress.toLowerCase()
  if (locationRequestCache.has(cacheKey)) return locationRequestCache.get(cacheKey)

  const request = axios.post(
    `${baseUrl}/scheduler-geocodes/resolve`,
    { address: normalizedAddress },
    getRequestHeaders()
  ).then((response) => {
    const location = { lat: Number(response?.data?.lat), lng: Number(response?.data?.lng) }
    return isValidMapLocation(location) ? location : null
  }).catch((error) => {
    console.error('Unable to map a teacher profile address:', error)
    return null
  })

  locationRequestCache.set(cacheKey, request)
  return request
}

const familyRecordsFrom = (teacher, events) => {
  const families = new Map()
  const addFamily = ({ familyId, familyName, address, students, location }) => {
    const addressLabel = formatMapAddress(address)
    if (!familyId && !addressLabel) return
    const key = text(familyId) || addressLabel.toLowerCase()
    const current = families.get(key) || {
      id: key,
      familyId: text(familyId),
      name: text(familyName) || 'Student family',
      address,
      addressLabel,
      location: null,
      students: [],
    }
    if (!current.addressLabel && addressLabel) {
      current.address = address
      current.addressLabel = addressLabel
    }
    current.location = current.location || location || getLocationFromRecord(address || {})
    const nextNames = (Array.isArray(students) ? students : [students])
      .map((student) => text(typeof student === 'string' ? student : student?.studentName || student?.name))
      .filter(Boolean)
    current.students = [...new Set([...current.students, ...nextNames])]
    families.set(key, current)
  }

  const roster = Array.isArray(teacher?.newStudentList) ? teacher.newStudentList : []
  roster.forEach((record) => addFamily({
    familyId: record?.familyId,
    familyName: record?.familyName || record?.lastName,
    address: record?.address || record?.familyAddress,
    students: Array.isArray(record?.students) ? record.students : record,
    location: getLocationFromRecord(record || {}),
  }))

  normalizeCalendarEvents(events).forEach((event) => addFamily({
    familyId: event?.familyId,
    familyName: event?.familyName,
    address: event?.familyAddress,
    students: event,
    location: getLocationFromRecord(event?.familyLocation || event?.familyAddress || {}),
  }))

  return Array.from(families.values())
}

const TeacherProfileMapCamera = ({ markers, selectedMarker }) => {
  const map = useMap()
  const markerKey = markers.map((marker) => `${marker.id}:${marker.location.lat}:${marker.location.lng}`).join('|')

  React.useEffect(() => {
    if (!map || markers.length === 0 || !window.google?.maps?.LatLngBounds) return
    const bounds = new window.google.maps.LatLngBounds()
    markers.forEach((marker) => bounds.extend(marker.location))
    if (markers.length === 1) {
      map.setCenter(markers[0].location)
      map.setZoom(11)
    } else {
      map.fitBounds(bounds, 42)
    }
    // markerKey represents every coordinate that affects the bounds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, markerKey])

  React.useEffect(() => {
    if (map && selectedMarker) map.panTo(selectedMarker.location)
  }, [map, selectedMarker])
  return null
}

const TeacherProfileMap = ({ teacher, events, calendarLoading }) => {
  const [markers, setMarkers] = React.useState([])
  const [selectedMarkerId, setSelectedMarkerId] = React.useState('')
  const [loading, setLoading] = React.useState(true)
  const [message, setMessage] = React.useState('')
  const familyRecords = React.useMemo(() => familyRecordsFrom(teacher, events), [events, teacher])

  React.useEffect(() => {
    let active = true
    const loadMarkers = async () => {
      setLoading(true)
      setMessage('')
      const teacherAddress = teacherAddressFrom(teacher)
      const teacherAddressLabel = teacherAddressLabelFrom(teacher)
      const savedTeacherLocation = getLocationFromRecord(teacher) || getLocationFromRecord(teacherAddress)
      const teacherLocation = isValidMapLocation(savedTeacherLocation)
        ? savedTeacherLocation
        : await resolveLocation(teacherAddressLabel)

      const familyMarkers = await Promise.all(familyRecords.map(async (family) => {
        const location = isValidMapLocation(family.location)
          ? family.location
          : await resolveLocation(family.addressLabel)
        if (!isValidMapLocation(location)) return null
        return { ...family, id: `family-${family.id}`, type: 'family', location }
      }))
      if (!active) return

      const nextMarkers = familyMarkers.filter(Boolean)
      if (isValidMapLocation(teacherLocation)) {
        nextMarkers.unshift({
          id: `teacher-${teacherIdFrom(teacher)}`,
          type: 'teacher',
          name: text(teacher?.Name || teacher?.name) || 'Teacher',
          addressLabel: teacherAddressLabel,
          location: teacherLocation,
          students: [],
        })
      }
      setMarkers(nextMarkers)
      setMessage(nextMarkers.length === 0 ? 'No mappable teacher or student addresses were found.' : '')
      setLoading(false)
    }
    loadMarkers()
    return () => { active = false }
  }, [familyRecords, teacher])

  const selectedMarker = markers.find((marker) => marker.id === selectedMarkerId) || null

  return (
    <div className='atharo-teacher__map-view'>
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
        <GoogleMap
          style={{ width: '100%', height: '100%' }}
          defaultCenter={DEFAULT_MAP_CENTER}
          defaultZoom={4}
          gestureHandling='greedy'
          disableDefaultUI
          mapId={GOOGLE_MAP_ID}
        >
          <TeacherProfileMapCamera markers={markers} selectedMarker={selectedMarker} />
          {markers.map((marker) => (
            <AdvancedMarker key={marker.id} position={marker.location} onClick={() => setSelectedMarkerId(marker.id)}>
              <Pin
                background={marker.type === 'teacher' ? '#14b8a6' : '#6366f1'}
                borderColor={marker.type === 'teacher' ? '#0f766e' : '#4338ca'}
                glyphColor='#fff'
                glyph={marker.type === 'teacher' ? 'T' : undefined}
                scale={selectedMarkerId === marker.id ? 1.28 : 1}
              />
            </AdvancedMarker>
          ))}
          {selectedMarker && (
            <InfoWindow position={selectedMarker.location} onCloseClick={() => setSelectedMarkerId('')}>
              <div className='atharo-teacher__map-info'>
                <strong>{selectedMarker.type === 'teacher' ? selectedMarker.name : `${selectedMarker.name} family`}</strong>
                <span>{selectedMarker.type === 'teacher' ? 'Teacher home' : 'Assigned students'}</span>
                {selectedMarker.addressLabel && <small><MdLocationOn /> {selectedMarker.addressLabel}</small>}
                {selectedMarker.students?.length > 0 && <small>{selectedMarker.students.join(', ')}</small>}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </APIProvider>
      {(loading || calendarLoading) && <div className='atharo-teacher__map-message'>Mapping teacher and student homes…</div>}
      {!loading && !calendarLoading && message && <div className='atharo-teacher__map-message is-error'>{message}</div>}
    </div>
  )
}

const TeacherProfile = ({ teacher, activeTab, setActiveTab, onViewFullProfile }) => {
  const [imageFailed, setImageFailed] = React.useState(false)
  const [calendarEvents, setCalendarEvents] = React.useState([])
  const [calendarLoading, setCalendarLoading] = React.useState(false)
  const [calendarError, setCalendarError] = React.useState('')
  const calendarRequested = React.useRef(false)
  const teacherId = teacherIdFrom(teacher)
  const image = getTeacherImageUrl(teacher)
  const instruments = teacherInstrumentsFrom(teacher)
  const teacherStatus = teacherStatusFrom(teacher)
  const backgroundCheck = backgroundCheckFrom(teacher)

  React.useEffect(() => {
    if (
      (activeTab !== 'schedule' && activeTab !== 'map')
      || !teacherId
      || calendarRequested.current
    ) return undefined
    let active = true
    calendarRequested.current = true
    setCalendarLoading(true)
    setCalendarError('')
    axios.get(`${baseUrl}/calendar/teacher/${encodeURIComponent(teacherId)}`, getRequestHeaders())
      .then((response) => {
        if (active) setCalendarEvents(Array.isArray(response?.data) ? response.data : [])
      })
      .catch((error) => {
        console.error('Unable to load the teacher schedule:', error)
        if (active) {
          setCalendarEvents([])
          setCalendarError('This teacher’s schedule is unavailable right now.')
        }
      })
      .finally(() => {
        if (active) setCalendarLoading(false)
      })
    return () => { active = false }
  }, [activeTab, teacherId])

  return (
    <div className='atharo-teacher'>
      <div className='atharo-teacher__scroll-area'>
        <section className='atharo-teacher__identity'>
          <span className='atharo-teacher__photo'>
            {image && !imageFailed
              ? <img src={image} alt='' onError={() => setImageFailed(true)} />
              : <span>{text(teacher?.Name).split(/\s+/).map((part) => part[0]).slice(0, 2).join('')}</span>}
          </span>
          <div className='atharo-teacher__identity-copy'>
            <small>{schoolNameFrom(teacher)}</small>
            <h2>{text(teacher?.Name || teacher?.name) || 'Teacher'}</h2>
            <div className='atharo-teacher__profile-badges' aria-label='Teacher status information'>
              <span className={`atharo-teacher__profile-badge is-${teacherStatusTone(teacherStatus)}`}>
                {teacherStatusLabel(teacherStatus)}
              </span>
              <span className={`atharo-teacher__profile-badge atharo-teacher__profile-badge--background is-${backgroundCheckTone(backgroundCheck)}`}>
                {backgroundCheckTone(backgroundCheck) === 'success' && <MdCheckCircle aria-hidden='true' />}
                Background: {backgroundCheckLabel(backgroundCheck)}
              </span>
            </div>
            <div className='atharo-teacher__instrument-list'>
              {instruments.map((instrument) => <span key={instrument}>{instrument}</span>)}
            </div>
          </div>
        </section>

        <div className='atharo-teacher__tabs' role='tablist' aria-label='Teacher profile sections'>
          {[
            ['contact', 'Contact Info'],
            ['schedule', 'Schedule'],
            ['map', 'Map'],
          ].map(([id, label]) => (
            <button
              type='button'
              role='tab'
              aria-selected={activeTab === id}
              className={activeTab === id ? 'is-active' : ''}
              onClick={() => setActiveTab(id)}
              key={id}
            >
              {label}
            </button>
          ))}
        </div>

        <div className='atharo-teacher__tab-content'>
          {activeTab === 'contact' && <TeacherContact teacher={teacher} />}
          {activeTab === 'schedule' && (
            <TeacherSchedule events={calendarEvents} loading={calendarLoading} error={calendarError} />
          )}
          {activeTab === 'map' && (
            <TeacherProfileMap teacher={teacher} events={calendarEvents} calendarLoading={calendarLoading} />
          )}
        </div>
      </div>

      <footer className='atharo-teacher__footer'>
        <button type='button' onClick={onViewFullProfile}>
          View Full Profile <MdOpenInNew aria-hidden='true' />
        </button>
      </footer>
    </div>
  )
}

export default TeacherProfile
